import { useId } from "react";

interface TaskJinnLogoProps {
  className?: string;
  size?: number | string;
  animateOnHover?: boolean;
}

export function TaskJinnLogo({
  className = "w-8 h-8",
  size,
  animateOnHover = true,
}: TaskJinnLogoProps) {
  const uniqueId = useId().replace(/:/g, "_");

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 100"
      fill="none"
      role="img"
      aria-label="TaskJinn Logo"
      width={size}
      height={size}
      className={`inline-block select-none overflow-visible transition-transform duration-300 ${
        animateOnHover ? "hover:scale-105 group-hover:scale-105" : ""
      } ${className}`}
    >
      <defs>
        <linearGradient
          id={`tjOrange_${uniqueId}`}
          x1="20"
          y1="10"
          x2="80"
          y2="60"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#f59e0b" />
          <stop offset="45%" stopColor="#d97757" />
          <stop offset="100%" stopColor="#c46445" />
        </linearGradient>

        <linearGradient
          id={`tjGold_${uniqueId}`}
          x1="30"
          y1="20"
          x2="70"
          y2="50"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#fde047" />
          <stop offset="50%" stopColor="#eab308" />
          <stop offset="100%" stopColor="#ca8a04" />
        </linearGradient>

        <linearGradient
          id={`tjBlue_${uniqueId}`}
          x1="15"
          y1="50"
          x2="85"
          y2="95"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="35%" stopColor="#2563eb" />
          <stop offset="75%" stopColor="#1d4ed8" />
          <stop offset="100%" stopColor="#1e3a8a" />
        </linearGradient>

        <linearGradient id={`tjCyanGlow_${uniqueId}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#a5f3fc" />
          <stop offset="100%" stopColor="#38bdf8" />
        </linearGradient>

        <linearGradient
          id={`tjBlueSwirl_${uniqueId}`}
          x1="20"
          y1="65"
          x2="80"
          y2="85"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#60a5fa" />
          <stop offset="50%" stopColor="#0284c7" />
          <stop offset="100%" stopColor="#1e40af" />
        </linearGradient>

        <filter id={`glow_${uniqueId}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>

      {/* Swirling Blue Hurricane Vortex */}
      <g className="transition-transform duration-500 ease-out origin-center">
        <path
          d="M22 58 C 30 52, 70 52, 78 58 C 84 62, 76 67, 60 68 C 42 69, 24 65, 22 58 Z"
          fill={`url(#tjBlue_${uniqueId})`}
          opacity="0.9"
        />
        <path
          d="M28 66 C 36 61, 64 61, 72 66 C 78 70, 70 75, 54 77 C 38 79, 24 73, 28 66 Z"
          fill={`url(#tjBlueSwirl_${uniqueId})`}
        />
        <path
          d="M20 59 C 28 53, 58 53, 76 60 C 82 62, 80 68, 68 72 C 54 77, 36 78, 30 73 C 25 69, 24 64, 28 61 C 36 57, 65 57, 72 63"
          stroke={`url(#tjBlue_${uniqueId})`}
          strokeWidth="4.5"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M35 75 C 41 71, 59 71, 65 75 C 69 78, 62 83, 50 84 C 38 85, 32 80, 35 75 Z"
          fill={`url(#tjBlue_${uniqueId})`}
        />
        <path
          d="M32 74 C 40 70, 60 70, 68 76 C 62 82, 42 85, 40 88 C 38 91, 56 92, 52 96 C 49 98, 46 96, 45 93 C 44 89, 48 85, 45 82 C 40 80, 33 77, 32 74 Z"
          fill={`url(#tjBlueSwirl_${uniqueId})`}
        />

        {/* Dynamic Wind Lines */}
        <path
          d="M16 63 C 24 57, 45 56, 62 61"
          stroke="#bae6fd"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.85"
        />
        <path
          d="M26 73 C 34 68, 52 68, 64 73"
          stroke="#bae6fd"
          strokeWidth="1.8"
          strokeLinecap="round"
          opacity="0.8"
        />
        <path
          d="M36 82 C 42 79, 56 79, 60 83"
          stroke="#e0f2fe"
          strokeWidth="1.5"
          strokeLinecap="round"
          opacity="0.9"
        />
      </g>

      {/* Orange Cartoon Robot Genie */}
      <g className="transition-transform duration-300 ease-out origin-center">
        {/* Tail emerging from vortex */}
        <path
          d="M44 54 C 43 59, 45 66, 49 70 C 51 66, 55 60, 56 54 Z"
          fill={`url(#tjOrange_${uniqueId})`}
        />
        <path
          d="M47 55 C 46 61, 48 67, 50 71 C 52 67, 54 62, 54 55 Z"
          fill={`url(#tjGold_${uniqueId})`}
          opacity="0.6"
        />

        {/* Torso */}
        <path
          d="M38 38 C 38 34, 42 33, 50 33 C 58 33, 62 34, 62 38 C 62 44, 57 56, 50 56 C 43 56, 38 44, 38 38 Z"
          fill={`url(#tjOrange_${uniqueId})`}
        />

        {/* Chest Plate / Core */}
        <path
          d="M44 38 L56 38 L53 46 L50 49 L47 46 Z"
          fill={`url(#tjGold_${uniqueId})`}
        />
        <circle cx="50" cy="43" r="2" fill="#ffffff" opacity="0.9" />

        {/* Golden Belt */}
        <ellipse cx="50" cy="53" rx="7" ry="2.2" fill={`url(#tjGold_${uniqueId})`} />

        {/* Arms */}
        <path
          d="M39 37 C 33 39, 28 44, 30 48 C 32 50, 35 48, 38 43"
          stroke={`url(#tjOrange_${uniqueId})`}
          strokeWidth="4.5"
          strokeLinecap="round"
          fill="none"
        />
        <circle cx="29" cy="48" r="2.8" fill={`url(#tjGold_${uniqueId})`} />
        <circle cx="28" cy="48" r="1.2" fill="#ffffff" />

        <path
          d="M61 37 C 67 38, 72 43, 71 47 C 70 50, 66 48, 63 43"
          stroke={`url(#tjOrange_${uniqueId})`}
          strokeWidth="4.5"
          strokeLinecap="round"
          fill="none"
        />
        <circle cx="71" cy="47" r="2.8" fill={`url(#tjGold_${uniqueId})`} />
        <circle cx="72" cy="47" r="1.2" fill="#ffffff" />

        {/* Neck */}
        <rect x="47" y="30" width="6" height="4" rx="1.5" fill={`url(#tjGold_${uniqueId})`} />

        {/* Head */}
        <rect x="36" y="15" width="28" height="18" rx="8" fill={`url(#tjOrange_${uniqueId})`} />
        <rect
          x="37.5"
          y="16.5"
          width="25"
          height="15"
          rx="6.5"
          stroke={`url(#tjGold_${uniqueId})`}
          strokeWidth="1.2"
          fill="none"
          opacity="0.7"
        />

        {/* Antenna */}
        <path
          d="M50 15 L50 9"
          stroke={`url(#tjOrange_${uniqueId})`}
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <circle
          cx="50"
          cy="8"
          r="3.2"
          fill={`url(#tjGold_${uniqueId})`}
          filter={`url(#glow_${uniqueId})`}
        />
        <circle cx="50" cy="8" r="1.6" fill="#ffffff" />

        {/* Face Visor */}
        <rect x="40" y="18.5" width="20" height="10.5" rx="4.5" fill="#1e293b" />

        {/* Glowing Cyan Eyes */}
        <ellipse
          cx="45"
          cy="23.5"
          rx="2.4"
          ry="2.8"
          fill={`url(#tjCyanGlow_${uniqueId})`}
          filter={`url(#glow_${uniqueId})`}
        />
        <circle cx="44.2" cy="22.5" r="0.9" fill="#ffffff" />

        <ellipse
          cx="55"
          cy="23.5"
          rx="2.4"
          ry="2.8"
          fill={`url(#tjCyanGlow_${uniqueId})`}
          filter={`url(#glow_${uniqueId})`}
        />
        <circle cx="54.2" cy="22.5" r="0.9" fill="#ffffff" />

        {/* Friendly Smile */}
        <path
          d="M47.5 26.5 C 48.5 27.8, 51.5 27.8, 52.5 26.5"
          stroke={`url(#tjCyanGlow_${uniqueId})`}
          strokeWidth="1.2"
          strokeLinecap="round"
          fill="none"
        />

        {/* Side Ear bolts */}
        <rect x="34" y="20" width="2.5" height="6" rx="1" fill={`url(#tjGold_${uniqueId})`} />
        <rect x="63.5" y="20" width="2.5" height="6" rx="1" fill={`url(#tjGold_${uniqueId})`} />
      </g>
    </svg>
  );
}

